import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { setCurrentUserCallbacks } from '../services/apiClient';

interface UserContextType {
  currentUserId: string;
  userAccount: any;
  userRole: string;
  handleLogout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

const ProductionUserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { accounts, instance } = useMsal();
  const userAccount = accounts[0];
  
  // Get Azure AD user ID - prefer oid (object ID) from the account
  const currentUserId = userAccount?.idTokenClaims?.oid || 
                       userAccount?.localAccountId || 
                       userAccount?.homeAccountId || 
                       '00000000-0000-0000-0000-000000000001';
  
  const userEmail = userAccount?.username || 'user@example.com';
  
  // This should come from your user context/API in a real app
  const userRole = 'admin';
  
  const handleLogout = () => {
    instance.logoutPopup();
  };

  // Set the callbacks for the API client
  useEffect(() => {
    setCurrentUserCallbacks(
      () => currentUserId,
      () => userEmail
    );
  }, [currentUserId, userEmail]);

  return (
    <UserContext.Provider value={{
      currentUserId,
      userAccount,
      userRole,
      handleLogout
    }}>
      {children}
    </UserContext.Provider>
  );
};

// Main User Provider - now always uses production (Azure AD)
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  return <ProductionUserProvider>{children}</ProductionUserProvider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};